import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { trackLandingEvent } from '../lib/analytics';
import { getStoredConsent, subscribeConsent } from '../lib/consent';
import { PREFERRED_SOURCE_URL, loadPreferredSource, type PreferredSourceApi } from '../lib/preferredSource';

const STORAGE_KEY = 'induce_preferred_source';
/** A dismissal is a "not now", not a "never" — this is the only entry point to the flow. */
const DISMISS_TTL_MS = 90 * 24 * 60 * 60 * 1000;
/** Engagement gates: whichever lands first opens the bar. */
const DWELL_MS = 12_000;
const SCROLL_VIEWPORTS = 1.2;

type Outcome = 'added' | 'dismissed';

function pagePath(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '/';
}

function isSuppressed(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { state?: Outcome; ts?: number };
    if (parsed.state === 'added') return true;
    if (parsed.state === 'dismissed') return Date.now() - (parsed.ts ?? 0) < DISMISS_TTL_MS;
    return false;
  } catch {
    return true; // Unreadable storage (private mode, blocked) — stay quiet rather than nag every load.
  }
}

function persist(state: Outcome): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, ts: Date.now() }));
  } catch {
    // Fail-open
  }
}

/**
 * Site-wide nudge to add inducelabs.ai as a Google preferred source.
 *
 * Deliberately mutually exclusive with <ConsentBanner />: it arms only once a consent
 * decision exists, so the two never share the bottom edge. Google's library is fetched
 * at the moment the bar arms, never on page load, and the CTA stays a real link to the
 * deeplink so it works even if that library never arrives.
 */
export function PreferredSourceBar() {
  const [consented, setConsented] = useState(false);
  const [engaged, setEngaged] = useState(false);
  const [suppressed, setSuppressed] = useState(true);
  const [visible, setVisible] = useState(false);
  const apiRef = useRef<PreferredSourceApi | null>(null);

  useEffect(() => {
    setSuppressed(isSuppressed());
    setConsented(getStoredConsent() !== null);
    return subscribeConsent(() => setConsented(true));
  }, []);

  useEffect(() => {
    const open = () => setEngaged(true);
    const onScroll = () => {
      if (window.scrollY > window.innerHeight * SCROLL_VIEWPORTS) open();
    };
    const timer = window.setTimeout(open, DWELL_MS);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (visible || suppressed || !consented || !engaged) return;
    setVisible(true);
    trackLandingEvent('preferred_source_impression', { location: 'sticky_bar', page_path: pagePath() });
    void loadPreferredSource().then((api) => {
      apiRef.current = api;
    });
  }, [visible, suppressed, consented, engaged]);

  /** Suppressing as well as hiding — otherwise the arming effect re-opens the bar on the next render. */
  const close = useCallback(() => {
    setSuppressed(true);
    setVisible(false);
  }, []);

  const handleClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    // Read synchronously: awaiting here would cost the user-gesture and the dialog would be blocked.
    const api = apiRef.current;
    trackLandingEvent('preferred_source_click', {
      location: 'sticky_bar',
      method: api ? 'sdk' : 'deeplink',
      page_path: pagePath(),
    });
    persist('added');

    if (api) {
      try {
        api.addPreferredSource();
        event.preventDefault(); // Only once the in-page dialog is actually underway.
      } catch {
        // Leave the click alone; the href carries the user to Google's preferences page.
      }
    }
    close();
  }, [close]);

  const handleDismiss = useCallback(() => {
    persist('dismissed');
    trackLandingEvent('preferred_source_dismiss', { location: 'sticky_bar', page_path: pagePath() });
    close();
  }, [close]);

  if (!visible) return null;

  return (
    <div className="preferred-source-dock fixed bottom-5 left-1/2 z-[150] -translate-x-1/2 max-sm:right-3 max-sm:bottom-3 max-sm:left-3 max-sm:translate-x-0">
      <div className="preferred-source-bar relative flex items-center gap-5 rounded border border-white/10 bg-black/90 px-4 py-3 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.9)] backdrop-blur-sm max-sm:flex-col max-sm:items-stretch max-sm:gap-3">
        <p className="m-0 text-[13px] leading-snug text-on-surface-variant max-sm:pr-8 max-sm:text-xs">
          Follow our releases in <span className="text-on-surface">Google Search</span>
        </p>

        <div className="flex shrink-0 items-center gap-2">
          <a
            href={PREFERRED_SOURCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            aria-label="Add Induce as a preferred source in Google Search"
            className="btn btn-ghost !px-4 !py-2 !text-[10px] sm:!text-xs max-sm:flex-1 max-sm:justify-center"
          >
            Add Induce as a preferred source
            <span className="arrow">→</span>
          </a>

          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="grid size-7 shrink-0 place-items-center rounded text-white/40 transition-colors hover:text-on-surface focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary max-sm:absolute max-sm:top-2 max-sm:right-2"
          >
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
              <path d="M1 1 10 10M10 1 1 10" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
